<template>
  <div class="template-card">
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-start justify-between">
        <div class="flex items-center space-x-3">
          <!-- Template Icon -->
          <div class="flex-shrink-0">
            <div
              class="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center"
            >
              <svg
                class="w-5 h-5 text-purple-600 dark:text-purple-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>

          <!-- Template Info -->
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {{ template.name }}
            </h3>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ formatToolName(template.toolName) }}
            </p>
          </div>
        </div>

        <!-- Actions Menu -->
        <div class="relative">
          <button
            class="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Template options"
            @click="showMenu = !showMenu"
          >
            <svg
              class="w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
              />
            </svg>
          </button>

          <!-- Dropdown Menu -->
          <div
            v-if="showMenu"
            class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10"
            @click.stop
          >
            <button
              class="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="editTemplate"
            >
              <svg
                class="w-4 h-4 mr-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              Edit Template
            </button>

            <button
              class="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="duplicateTemplate"
            >
              <svg
                class="w-4 h-4 mr-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Duplicate
            </button>

            <div class="border-t border-gray-200 dark:border-gray-700 my-1" />

            <button
              class="flex items-center w-full px-4 py-2 text-sm text-error-600 dark:text-error-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              @click="deleteTemplate"
            >
              <svg
                class="w-4 h-4 mr-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="mt-3 flex-1">
        <p class="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
          {{ template.description || 'No description provided' }}
        </p>
      </div>

      <!-- Metadata -->
      <div class="mt-3 space-y-2">
        <!-- Creation Date -->
        <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
          <svg
            class="w-3 h-3 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          Created {{ formatDate(template.createdAt) }}
        </div>

        <!-- Usage Count -->
        <div
          v-if="template.usageCount"
          class="flex items-center text-xs text-gray-500 dark:text-gray-400"
        >
          <svg
            class="w-3 h-3 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          Used {{ template.usageCount }} times
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="mt-4 flex items-center justify-between">
        <!-- Tags -->
        <div class="flex flex-wrap gap-1">
          <span
            v-for="tag in template.tags?.slice(0, 2)"
            :key="tag"
            class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
          >
            {{ tag }}
          </span>
          <span
            v-if="template.tags && template.tags.length > 2"
            class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
          >
            +{{ template.tags.length - 2 }}
          </span>
        </div>

        <!-- Use Button -->
        <button
          class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/50 dark:text-purple-300 dark:hover:bg-purple-900/70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
          @click="useTemplate"
        >
          <svg
            class="w-3 h-3 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h1m4 0h1M9 18h6"
            />
          </svg>
          Use Template
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

export interface Template {
  id: string;
  name: string;
  description?: string;
  toolName: string;
  parameters: Record<string, any>;
  tags?: string[];
  createdAt: string;
  updatedAt?: string;
  usageCount?: number;
}

export interface TemplateCardProps {
  template: Template;
}

const props = defineProps<TemplateCardProps>();

const emit = defineEmits<{
  use: [template: Template];
  edit: [template: Template];
  delete: [template: Template];
}>();

const showMenu = ref(false);

// Methods
const formatToolName = (toolName: string): string => {
  return toolName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return 'today';
  } else if (diffInDays === 1) {
    return 'yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

const useTemplate = () => {
  showMenu.value = false;
  emit('use', props.template);
};

const editTemplate = () => {
  showMenu.value = false;
  emit('edit', props.template);
};

const duplicateTemplate = () => {
  showMenu.value = false;
  // Create a copy with a new name
  const duplicatedTemplate = {
    ...props.template,
    id: `${props.template.id}_copy`,
    name: `${props.template.name} (Copy)`,
    createdAt: new Date().toISOString(),
    usageCount: 0,
  };
  console.log('Duplicate template:', duplicatedTemplate);
};

const deleteTemplate = () => {
  showMenu.value = false;
  if (confirm(`Are you sure you want to delete the template "${props.template.name}"?`)) {
    emit('delete', props.template);
  }
};

// Close menu when clicking outside
const handleClickOutside = () => {
  showMenu.value = false;
};

// Add event listener for clicking outside
if (typeof window !== 'undefined') {
  document.addEventListener('click', handleClickOutside);
}
</script>

<style scoped>
.template-card {
  @apply bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700;
  @apply hover:border-purple-300 dark:hover:border-purple-600;
  @apply transition-all duration-200;
  @apply p-4;
}

/* Line clamp utility for description */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .template-card {
    @apply border-2;
  }
}

/* Print styles */
@media print {
  .template-card {
    break-inside: avoid;
  }
}
</style>
